import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import { useTranslation } from "react-i18next";
import { EmployeeUser } from "../../types";
import { useState } from "react";


interface ScheduleEmployeeProps {
  employee: EmployeeUser;
}

export function ScheduleEmployee({ employee }: ScheduleEmployeeProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const formatDayName = (day: number): string => {
    const days = [
      t("employee.schedule.days.0"),
      t("employee.schedule.days.1"),
      t("employee.schedule.days.2"),
      t("employee.schedule.days.3"),
      t("employee.schedule.days.4"),
      t("employee.schedule.days.5"),
      t("employee.schedule.days.6"),
    ];
    return days[day];
  };

  const handleChange = () => {
    setExpanded(!expanded);
  };

  return (
    <div className="flex flex-col gap-3">
      <Accordion 
        expanded={expanded} 
        onChange={handleChange}
        sx={{
          boxShadow: 'none',
          '&:before': {
            display: 'none',
          },
          backgroundColor: 'transparent',
        }}
      >
        <AccordionSummary
          expandIcon={
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s ease-in-out'
              }}
            >
              <path
                d="M5.707 9.71a1 1 0 0 0 0 1.415l4.892 4.887a2 2 0 0 0 2.828 0l4.89-4.89a1 1 0 1 0-1.414-1.415l-4.185 4.186a1 1 0 0 1-1.415 0L7.121 9.71a1 1 0 0 0-1.414 0Z"
                fill="#EF0BB8"
              />
            </svg>
          }
          sx={{
            padding: 0,
            minHeight: '40px',
            '& .MuiAccordionSummary-content': {
              margin: 0,
            },
          }}
        >
          <h2 className="text-base font-montserrat font-medium text-gray-800">
            {t("employee.schedule.title")}
          </h2>
        </AccordionSummary>
        <AccordionDetails sx={{ padding: '12px 0' }}>
          {employee.workSchedules && employee.workSchedules.length > 0 ? (
            <div className="flex flex-col gap-3">
              {employee.workSchedules?.map((schedule, index) => (
                <div 
                  key={index}
                  className="flex flex-col w-full bg-white rounded-lg border border-gray-200 p-3 hover:border-[#EF0BB8] transition-colors duration-200"
                >
                  <div className="flex justify-between items-center w-full mb-2">
                    <h3 className="font-montserrat font-semibold text-sm text-gray-800">
                      {schedule.name}
                    </h3>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2 w-full">
                    {schedule.days.map((day, idx) => (
                      <div key={idx} className="flex flex-col gap-1">
                        <span className="text-xs font-medium text-gray-500">
                          {formatDayName(day.dayOfWeek)}
                        </span>
                        {day.blocks && day.blocks.length > 0 ? (
                          <div className="flex flex-wrap gap-1">
                            {day.blocks.map((block, blockIndex) => (
                              <span
                                key={blockIndex}
                                className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-montserrat bg-[#EF0BB8] bg-opacity-10 text-[#EF0BB8]"
                              >
                                {block.startTime.substring(0, 5)} - {block.endTime.substring(0, 5)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-montserrat text-gray-400">
                            {t("schedule.day-off")}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-sm text-gray-500">
              {t("employee.schedule.no-schedule")}
            </div>
          )}
        </AccordionDetails>
      </Accordion>
    </div>
  );
} 